/**
 * AdminBar — floating helper bar for logged-in admins on public pages.
 *
 * Shows on every public route (bypasses /admin/*, /links/*, /speaker-invite/*) when
 * admin_token is in localStorage. Provides:
 *   - "Edit this page →" jumping to the matching admin editor
 *   - Persistent Coming Soon indicator with one-click "Turn off"
 *   - Quick access to admin dashboard + logout
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Edit, ExternalLink, LayoutDashboard, LogOut, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map public-page paths → admin editor paths + labels
const PAGE_MAP = [
  { match: /^\/$/, adminPath: '/admin/homepage', label: 'Homepage' },
  { match: /^\/program/, adminPath: '/admin/program', label: 'Program' },
  { match: /^\/speakers/, adminPath: '/admin/speakers', label: 'Speakers' },
  { match: /^\/sponsorship/, adminPath: '/admin/homepage', label: 'Sponsorship page' },
  { match: /^\/golf/, adminPath: '/admin/homepage', label: 'Golf page' },
  { match: /^\/about/, adminPath: '/admin/homepage', label: 'About section' },
  { match: /^\/attend/, adminPath: '/admin/homepage', label: 'Attend section' },
  { match: /^\/exhibit/, adminPath: '/admin/homepage', label: 'Exhibit section' },
];

const resolveEditor = (pathname) => {
  const hit = PAGE_MAP.find((row) => row.match.test(pathname));
  return hit || { adminPath: '/admin/homepage', label: 'this page' };
};

const AdminBar = () => {
  const location = useLocation();
  const [tokenPresent, setTokenPresent] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Watch token changes (login/logout in another tab)
  useEffect(() => {
    const check = () => setTokenPresent(!!localStorage.getItem('admin_token'));
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  // Fetch coming soon status
  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/content/coming-soon/status`);
      if (r.ok) setComingSoon((await r.json()).enabled);
    } catch { /* no-op */ }
  }, []);

  useEffect(() => {
    if (tokenPresent) fetchStatus();
  }, [tokenPresent, fetchStatus]);

  // Hide on admin/links/speaker-invite paths
  const path = location.pathname;
  const hideOn = path.startsWith('/admin') || path.startsWith('/links/') || path.startsWith('/speaker-invite');
  const barVisible = tokenPresent && !hideOn && !dismissed;

  // When the fixed bar is visible, push down anything else that is also fixed at top-0
  // (site navigation) so it doesn't hide behind our bar. We do this by toggling a body
  // class and having a global CSS rule offset every direct child of body with `top: 0`.
  useEffect(() => {
    if (!barVisible) return undefined;
    const cls = 'admin-bar-active';
    document.body.classList.add(cls);

    // Inject the CSS rule once
    const styleId = 'admin-bar-offset-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        body.admin-bar-active { padding-top: 40px; }
        body.admin-bar-active nav.fixed.top-0,
        body.admin-bar-active header.fixed.top-0,
        body.admin-bar-active nav[class*="top-0"][class*="fixed"] { top: 40px !important; }
      `;
      document.head.appendChild(style);
    }
    return () => { document.body.classList.remove(cls); };
  }, [barVisible]);

  if (!barVisible) return null;

  const editor = resolveEditor(path);

  const turnOffComingSoon = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await fetch(`${API_URL}/api/content/coming-soon`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: false }),
      });
      if (!r.ok) throw new Error('Failed');
      setComingSoon(false);
      toast.success('Coming Soon mode turned off. Public site is now live.');
    } catch (e) {
      toast.error('Failed to turn off Coming Soon. Try from the admin panel.');
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setTokenPresent(false);
    toast.success('Logged out');
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9998] shadow-lg print:hidden"
      style={{
        background: comingSoon
          ? 'linear-gradient(90deg, #b45309 0%, #d97706 100%)'
          : 'linear-gradient(90deg, #0f172a 0%, #1e3a8a 100%)',
      }}
      data-testid="admin-bar"
    >
      <div className="max-w-[1600px] mx-auto px-4 py-2 flex items-center gap-3 text-white text-sm">
        {comingSoon && (
          <div className="flex items-center gap-1.5 font-semibold" data-testid="admin-bar-coming-soon-indicator">
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">Coming Soon mode is ON</span>
            <span className="sm:hidden">CS ON</span>
            <button
              onClick={turnOffComingSoon}
              disabled={busy}
              className="ml-1 underline underline-offset-2 hover:text-yellow-200 disabled:opacity-60"
              data-testid="admin-bar-turnoff-coming-soon"
            >
              {busy ? 'Turning off…' : 'Turn off'}
            </button>
            <span className="mx-2 opacity-40">|</span>
          </div>
        )}

        <a
          href={editor.adminPath}
          className="inline-flex items-center gap-1.5 font-medium hover:text-yellow-300 transition-colors"
          data-testid="admin-bar-edit-link"
        >
          <Edit className="w-4 h-4" />
          Edit {editor.label} →
        </a>

        <span className="opacity-40">|</span>

        <a
          href="/admin/homepage"
          className="hidden md:inline-flex items-center gap-1.5 opacity-90 hover:opacity-100 hover:text-yellow-300 transition-colors"
          data-testid="admin-bar-dashboard-link"
        >
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </a>

        <div className="flex-1" />

        {!comingSoon && (
          <span className="hidden lg:inline text-xs opacity-60">
            You&apos;re signed in as admin — visible to you only
          </span>
        )}

        <button
          onClick={logout}
          className="inline-flex items-center gap-1 opacity-80 hover:opacity-100 text-xs"
          data-testid="admin-bar-logout"
          title="Log out"
        >
          <LogOut className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Log out</span>
        </button>

        <button
          onClick={() => setDismissed(true)}
          className="opacity-60 hover:opacity-100 p-1"
          data-testid="admin-bar-dismiss"
          title="Dismiss (until reload)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AdminBar;
